import { loadStripe } from '@stripe/stripe-js';
import {
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { When } from 'react-if';

import { env } from '../../../common/lib/env';
import { Modal } from '../../../common/Atoms/Modal';
import { SmallSpinner } from '../../../common/Atoms/SmallSpinner';
import { Paragraph } from '../../../common/Atoms/Typography/Paragraph';
import { emptySplitApi } from '../../services/baseApi';
import { PortalCacheTags } from '../../services/cacheTags';

import { EmbeddedCheckoutFrame } from './EmbeddedCheckoutFrame';

interface IStripeCheckoutProps {
  code: string;
  connectedAccountId: string;
  onClose: () => void;
  onPaid: () => void;
  mode: `invoice` | `statement`;
  invoiceIds?: string[];
  otherAmount?: number;
  payNextInstalment?: boolean;
  paymentMethod: `card` | `dd`;
}

export function StripeCheckout({ code, connectedAccountId, onClose, onPaid, mode, invoiceIds, otherAmount, payNextInstalment, paymentMethod }: IStripeCheckoutProps) {
  const [checkout, setCheckout] = useState<{ clientSecret: string; sessionId: string } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const dispatch = useDispatch();

  const stripePromise = useMemo(() => {
    // Cant load outside as we need the stripe account
    return loadStripe(env.stripePublicKey, {
      stripeAccount: connectedAccountId,
    });
  }, [connectedAccountId]);

  useEffect(() => {
    const data = {
      invoiceIds: invoiceIds,
      otherAmount: otherAmount,
      payNextInstalment,
      paymentMethod,
    };

    // Create a Checkout Session as soon as the page loads
    axios(`${env.basePortalApiUrl}${mode}/pay/stripe/${code}`, {
      method: `POST`,
      data,
      withCredentials: true,
    })
      .then(res => setCheckout(res.data));
  }, []);

  // Record the payment before closing so the invoice shows as paid straight away instead of waiting on the webhook
  function onComplete() {
    setSyncing(true);

    axios(`${env.basePortalApiUrl}stripe-checkout/${checkout?.sessionId}/sync`, {
      method: `POST`,
      withCredentials: true,
    })
      // The payment went through either way, the webhook and mop-up retry the sync
      .catch(err => console.error(err))
      .finally(() => {
        dispatch(emptySplitApi.util.invalidateTags([PortalCacheTags.Invoice, PortalCacheTags.Contact]));
        onPaid();
      });
  }

  return (
    <div id={ `checkout` }>
      { checkout && (
        <Modal
          open={ true }
          setOpen={ syncing ? () => undefined : onClose }
          title={ `` }
          width={ `stripe` }
        >
          <When condition={ syncing }>
            <div className={ `flex items-center justify-center gap-2 p-4` }>
              <SmallSpinner colored />
              <Paragraph>
                { `Confirming your payment, please don't close this window...` }
              </Paragraph>
            </div>
          </When>
          <EmbeddedCheckoutFrame
            stripePromise={ stripePromise }
            clientSecret={ checkout.clientSecret }
            onComplete={ onComplete }
          />
        </Modal>
      ) }
    </div>
  );
}
